import { lazy, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet, type RouteObject, useParams } from "react-router-dom";
import { ClassicSubscriptionGate } from "@/features/billing/ClassicGate";
import { RequireStoreRole, RequireVendor } from "@/features/vendor/guards";
import { HomePage } from "@/pages/HomePage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { RouteError } from "./ErrorBoundary";
import { RequireAuth, RequireGuest } from "./guards";
import { STATIC_PAGE_SLUGS } from "@/lib/siteLinks";
import { Lazy } from "./Lazy";
import { AuthLayout } from "./layouts/AuthLayout";
import { StoreLayout } from "./layouts/StoreLayout";

const UiGalleryPage = lazy(() => import("@/pages/dev/UiGalleryPage").then((m) => ({ default: m.UiGalleryPage })));
const ShellPreview = lazy(() => import("@/pages/dev/UiGalleryPage").then((m) => ({ default: m.ShellPreview })));
const VendorLayout = lazy(() => import("./layouts/VendorLayout").then((m) => ({ default: m.VendorLayout })));
const page = <K extends string>(loader: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));
const AccountPage = page(() => import("@/pages/account/AccountPage"), "AccountPage");
const ForgotPasswordPage = page(() => import("@/pages/auth/ForgotPasswordPage"), "ForgotPasswordPage");
const LoginPage = page(() => import("@/pages/auth/LoginPage"), "LoginPage");
const RegisterPage = page(() => import("@/pages/auth/RegisterPage"), "RegisterPage");
const ResetPasswordPage = page(() => import("@/pages/auth/ResetPasswordPage"), "ResetPasswordPage");
const VerifyPage = page(() => import("@/pages/auth/VerifyPage"), "VerifyPage");
const SearchPage = page(() => import("@/pages/SearchPage"), "SearchPage");
const ProductPage = page(() => import("@/pages/ProductPage"), "ProductPage");
const StorePage = page(() => import("@/pages/StorePage"), "StorePage");
const SellPage = page(() => import("@/pages/SellPage"), "SellPage");
const OnboardingPage = page(() => import("@/pages/vendor/OnboardingPage"), "OnboardingPage");
const VendorDashboardPage = page(() => import("@/pages/vendor/DashboardPage"), "VendorDashboardPage");
const VendorListingsPage = page(() => import("@/pages/vendor/ListingsPage"), "VendorListingsPage");
const ListingEditorPage = page(() => import("@/pages/vendor/ListingEditorPage"), "ListingEditorPage");
const BulkUploadPage = page(() => import("@/pages/vendor/BulkUploadPage"), "BulkUploadPage");
const SubscriptionPage = page(() => import("@/pages/vendor/SubscriptionPage"), "SubscriptionPage");
const KycPage = page(() => import("@/pages/vendor/KycPage"), "KycPage");
const StaffPage = page(() => import("@/pages/vendor/StaffPage"), "StaffPage");
const StoreSettingsPage = page(() => import("@/pages/vendor/SettingsPage"), "StoreSettingsPage");
const CartPage = page(() => import("@/pages/CartPage"), "CartPage");
const CheckoutPage = page(() => import("@/pages/CheckoutPage"), "CheckoutPage");
const PaymentCallbackPage = page(() => import("@/pages/PaymentCallbackPage"), "PaymentCallbackPage");
const SharedWishlistPage = page(() => import("@/pages/SharedWishlistPage"), "SharedWishlistPage");
const OrdersPage = page(() => import("@/pages/account/OrdersPage"), "OrdersPage");
const OrderDetailPage = page(() => import("@/pages/account/OrderDetailPage"), "OrderDetailPage");
const WishlistPage = page(() => import("@/pages/account/WishlistPage"), "WishlistPage");
const AccountWalletPage = page(() => import("@/pages/WalletPage"), "AccountWalletPage");
const StoreWalletPage = page(() => import("@/pages/WalletPage"), "StoreWalletPage");
const WithdrawPage = page(() => import("@/pages/vendor/WithdrawPage"), "WithdrawPage");
const VendorOrdersPage = page(() => import("@/pages/vendor/VendorOrdersPage"), "VendorOrdersPage");
const VendorOrderDetailPage = page(() => import("@/pages/vendor/VendorOrderDetailPage"), "VendorOrderDetailPage");
const VendorReviewsPage = page(() => import("@/pages/vendor/VendorReviewsPage"), "VendorReviewsPage");
const NotificationsPage = page(() => import("@/pages/NotificationsPage"), "NotificationsPage");
const ChatsPage = page(() => import("@/pages/ChatsPage"), "ChatsPage");
const MorePage = page(() => import("@/pages/MorePage"), "MorePage");
const ChatSettingsPage = page(() => import("@/pages/ChatSettingsPage"), "ChatSettingsPage");
const DealsPage = page(() => import("@/pages/DealsPage"), "DealsPage");
const DropPage = page(() => import("@/pages/DealsPage"), "DropPage");
const ComparePage = page(() => import("@/pages/ComparePage"), "ComparePage");
const RewardsPage = page(() => import("@/pages/account/RewardsPage"), "RewardsPage");
const AlertsPage = page(() => import("@/pages/account/AlertsPage"), "AlertsPage");
const VendorOffersPage = page(() => import("@/pages/vendor/VendorOffersPage"), "VendorOffersPage");
const VendorAdsPage = page(() => import("@/pages/vendor/ads/VendorAdsPage"), "VendorAdsPage");
const CampaignEditorPage = page(() => import("@/pages/vendor/ads/CampaignEditorPage"), "CampaignEditorPage");
const CampaignDetailPage = page(() => import("@/pages/vendor/ads/CampaignDetailPage"), "CampaignDetailPage");
const VendorDropsPage = page(() => import("@/pages/vendor/VendorDropsPage"), "VendorDropsPage");
const VendorCouponsPage = page(() => import("@/pages/vendor/VendorCouponsPage"), "VendorCouponsPage");
const BillingHomePage = page(() => import("@/pages/billing/BillingHomePage"), "BillingHomePage");
const ChoosePlanPage = page(() => import("@/pages/billing/ChoosePlanPage"), "ChoosePlanPage");
const PayTransferPage = page(() => import("@/pages/billing/PayTransferPage"), "PayTransferPage");
const UploadProofPage = page(() => import("@/pages/billing/UploadProofPage"), "UploadProofPage");
const PaymentStatusPage = page(() => import("@/pages/billing/PaymentStatusPage"), "PaymentStatusPage");
const NotificationSettingsPage = page(() => import("@/pages/NotificationSettingsPage"), "NotificationSettingsPage");
const AdminPaymentReviewPage = page(() => import("@/pages/admin/PaymentReviewPage"), "AdminPaymentReviewPage");
const AdminPaymentSettingsPage = page(() => import("@/pages/admin/PaymentReviewPage"), "AdminPaymentSettingsPage");
const AdminBroadcastPage = page(() => import("@/pages/admin/BroadcastPage"), "AdminBroadcastPage");
const AdminLayout = lazy(() => import("./layouts/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const RequireAdmin = lazy(() => import("@/features/admin/RequireAdmin").then((m) => ({ default: m.RequireAdmin })));
const AdminDashboardPage = page(() => import("@/pages/admin/DashboardPage"), "AdminDashboardPage");
const AdminUsersPage = page(() => import("@/pages/admin/UsersPage"), "AdminUsersPage");
const AdminVendorsPage = page(() => import("@/pages/admin/VendorsPage"), "AdminVendorsPage");
const AdminKycPage = page(() => import("@/pages/admin/KycPage"), "AdminKycPage");
const AdminListingsPage = page(() => import("@/pages/admin/ListingsPage"), "AdminListingsPage");
const AdminCataloguePage = page(() => import("@/pages/admin/CataloguePage"), "AdminCataloguePage");
const AdminOrdersPage = page(() => import("@/pages/admin/OrdersPage"), "AdminOrdersPage");
const AdminDisputesPage = page(() => import("@/pages/admin/DisputesPage"), "AdminDisputesPage");
const AdminDisputeDetailPage = page(() => import("@/pages/admin/DisputesPage"), "AdminDisputeDetailPage");
const AdminPaymentsPage = page(() => import("@/pages/admin/PaymentsPage"), "AdminPaymentsPage");
const AdminWalletsPage = page(() => import("@/pages/admin/WalletsPage"), "AdminWalletsPage");
const AdminPayoutsPage = page(() => import("@/pages/admin/PayoutsPage"), "AdminPayoutsPage");
const AdminSubscriptionsPage = page(() => import("@/pages/admin/SubscriptionsPage"), "AdminSubscriptionsPage");
const AdminPlansPage = page(() => import("@/pages/admin/PlansPage"), "AdminPlansPage");
const AdminAdsPage = page(() => import("@/pages/admin/AdsPage"), "AdminAdsPage");
const AdminPromotionsPage = page(() => import("@/pages/admin/PromotionsPage"), "AdminPromotionsPage");
const AdminCmsPage = page(() => import("@/pages/admin/CmsPage"), "AdminCmsPage");
const AdminNotificationsPage = page(() => import("@/pages/admin/NotificationsPage"), "AdminNotificationsPage");
const AdminReviewsPage = page(() => import("@/pages/admin/ReviewsPage"), "AdminReviewsPage");
const AdminStorePhotosPage = page(() => import("@/pages/admin/StorePhotosPage"), "AdminStorePhotosPage");
const AdminChatsPage = page(() => import("@/pages/admin/ChatsPage"), "AdminChatsPage");
const AdminSupportPage = page(() => import("@/pages/admin/SupportPage"), "AdminSupportPage");
const AdminReportsPage = page(() => import("@/pages/admin/ReportsPage"), "AdminReportsPage");
const AdminSettingsPage = page(() => import("@/pages/admin/SettingsPage"), "AdminSettingsPage");
const AdminTeamPage = page(() => import("@/pages/admin/TeamPage"), "AdminTeamPage");
const AdminAuditLogPage = page(() => import("@/pages/admin/AuditLogPage"), "AdminAuditLogPage");
const SupportPage = page(() => import("@/pages/SupportPage"), "SupportPage");
const SupportTicketPage = page(() => import("@/pages/SupportPage"), "SupportTicketPage");
const StaticPage = lazy(() => import("@/pages/ContentPage").then((m) => ({ default: m.StaticPage })));
const FaqPage = page(() => import("@/pages/ContentPage"), "FaqPage");
const AdvertisePage = page(() => import("@/pages/advertise/AdvertisePage"), "AdvertisePage");
const AdWizardPage = page(() => import("@/pages/advertise/AdWizardPage"), "AdWizardPage");
const MyAdsPage = page(() => import("@/pages/advertise/MyAdsPage"), "MyAdsPage");
const MyAdDetailPage = page(() => import("@/pages/advertise/MyAdsPage"), "MyAdDetailPage");

/** Old chat links (/messages/…, /vendor/messages/…) open the new chats. */
function ChatRedirect({ selling = false }: { selling?: boolean }) {
  const { id } = useParams();
  return <Navigate to={id ? `/chats/${id}` : selling ? "/chats?filter=selling" : "/chats"} replace />;
}

function lazyEl(node: ReactNode) {
  return <Lazy>{node}</Lazy>;
}
function role(path: string, node: ReactNode) {
  return <RequireStoreRole path={path}>{node}</RequireStoreRole>;
}
type AdminModule = import("@/features/admin/hooks").ModuleKey;
function admin(permission: AdminModule, node: ReactNode) {
  return <RequireAdmin permission={permission}>{node}</RequireAdmin>;
}

const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      {
        path: "/__ui",
        element: <StoreLayout />,
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <UiGalleryPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        path: "/__ui/vendor-shell",
        element: (
          <Lazy>
            <VendorLayout>
              <ShellPreview kind="vendor" />
            </VendorLayout>
          </Lazy>
        ),
      },
    ]
  : [];

const appRoutes: RouteObject[] = [
  {
    element: <StoreLayout />,
    children: [
      { path: "/", element: <HomePage /> },
      { path: "/search", element: lazyEl(<SearchPage />) },
      { path: "/c/:categorySlug", element: lazyEl(<SearchPage />) },
      { path: "/p/:productSlug", element: lazyEl(<ProductPage />) },
      { path: "/store/:vendorSlug", element: lazyEl(<StorePage />) },
      { path: "/sell", element: lazyEl(<SellPage />) },
      { path: "/cart", element: lazyEl(<CartPage />) },
      { path: "/w/:shareToken", element: lazyEl(<SharedWishlistPage />) },
      { path: "/deals", element: lazyEl(<DealsPage />) },
      { path: "/deals/:slug", element: lazyEl(<DropPage />) },
      { path: "/compare", element: lazyEl(<ComparePage />) },
      { path: "/advertise", element: lazyEl(<AdvertisePage />) },
      { path: "/more", element: lazyEl(<MorePage />) },
      ...STATIC_PAGE_SLUGS.map((slug) => ({
        path: `/${slug}`,
        element: lazyEl(<StaticPage key={slug} slug={slug} />),
      })),
      { path: "/faq", element: lazyEl(<FaqPage />) },
      {
        element: <RequireAuth />,
        children: [
          { path: "/account", element: lazyEl(<AccountPage />) },
          { path: "/checkout", element: lazyEl(<CheckoutPage />) },
          { path: "/payment/callback", element: lazyEl(<PaymentCallbackPage />) },
          { path: "/account/orders", element: lazyEl(<OrdersPage />) },
          { path: "/account/orders/:number", element: lazyEl(<OrderDetailPage />) },
          { path: "/account/wishlist", element: lazyEl(<WishlistPage />) },
          { path: "/account/wallet", element: lazyEl(<AccountWalletPage />) },
          { path: "/account/rewards", element: lazyEl(<RewardsPage />) },
          { path: "/account/alerts", element: lazyEl(<AlertsPage />) },
          { path: "/notifications", element: lazyEl(<NotificationsPage />) },
          { path: "/settings/notifications", element: lazyEl(<NotificationSettingsPage />) },
          { path: "/support", element: lazyEl(<SupportPage />) },
          { path: "/support/:number", element: lazyEl(<SupportTicketPage />) },
          { path: "/chats", element: lazyEl(<ChatsPage />) },
          { path: "/chats/:id", element: lazyEl(<ChatsPage />) },
          { path: "/messages", element: <ChatRedirect /> },
          { path: "/messages/:id", element: <ChatRedirect /> },
          { path: "/settings/chat", element: lazyEl(<ChatSettingsPage />) },
          { path: "/vendor/onboarding", element: lazyEl(<OnboardingPage />) },
          { path: "/advertise/new", element: lazyEl(<AdWizardPage key="new" />) },
          { path: "/ads", element: lazyEl(<MyAdsPage />) },
          { path: "/ads/:id", element: lazyEl(<MyAdDetailPage />) },
          { path: "/ads/:id/edit", element: lazyEl(<AdWizardPage />) },
        ],
      },
    ],
  },
  {
    path: "/vendor",
    element: (
      <RequireVendor>
        <Lazy>
          <VendorLayout>
            <Lazy>
              <Outlet />
            </Lazy>
          </VendorLayout>
        </Lazy>
      </RequireVendor>
    ),
    children: [
      { index: true, element: <VendorDashboardPage /> },
      { path: "listings", element: role("/vendor/listings", <VendorListingsPage />) },
      { path: "listings/new", element: role("/vendor/listings", <ListingEditorPage key="new" />) },
      { path: "listings/bulk", element: role("/vendor/listings/bulk", <BulkUploadPage />) },
      { path: "listings/:id", element: role("/vendor/listings", <ListingEditorPage />) },
      { path: "orders", element: role("/vendor/orders", <VendorOrdersPage />) },
      { path: "orders/:id", element: role("/vendor/orders", <VendorOrderDetailPage />) },
      { path: "reviews", element: role("/vendor/reviews", <VendorReviewsPage />) },
      { path: "messages", element: <ChatRedirect selling /> },
      { path: "messages/:id", element: <ChatRedirect selling /> },
      { path: "offers", element: role("/vendor/offers", <VendorOffersPage />) },
      { path: "ads", element: role("/vendor/ads", <VendorAdsPage />) },
      { path: "ads/new", element: role("/vendor/ads", <CampaignEditorPage key="new" />) },
      { path: "ads/:id", element: role("/vendor/ads", <CampaignDetailPage />) },
      { path: "ads/:id/edit", element: role("/vendor/ads", <CampaignEditorPage />) },
      { path: "drops", element: role("/vendor/drops", <VendorDropsPage />) },
      { path: "coupons", element: role("/vendor/coupons", <VendorCouponsPage />) },
      { path: "wallet", element: role("/vendor/wallet", <StoreWalletPage />) },
      { path: "wallet/withdraw", element: role("/vendor/wallet/withdraw", <WithdrawPage />) },
      {
        path: "subscription",
        element: role(
          "/vendor/subscription",
          <ClassicSubscriptionGate>
            <SubscriptionPage />
          </ClassicSubscriptionGate>,
        ),
      },
      { path: "kyc", element: role("/vendor/kyc", <KycPage />) },
      { path: "staff", element: role("/vendor/staff", <StaffPage />) },
      { path: "settings", element: role("/vendor/settings", <StoreSettingsPage />) },
    ],
  },
  {
    // Full-screen billing flow (no dashboard chrome or bottom nav inside).
    path: "/billing",
    element: (
      <RequireVendor>
        <RequireStoreRole path="/billing">
          <Lazy>
            <Outlet />
          </Lazy>
        </RequireStoreRole>
      </RequireVendor>
    ),
    children: [
      { index: true, element: <BillingHomePage /> },
      { path: "plans", element: <ChoosePlanPage /> },
      { path: "pay/:id", element: <PayTransferPage /> },
      { path: "pay/:id/proof", element: <UploadProofPage /> },
      { path: "pay/:id/status", element: <PaymentStatusPage /> },
    ],
  },
  {
    // Wallet top-up by bank transfer: the same pay → proof → review screens, for any signed-in user.
    path: "/wallet/topup",
    element: (
      <RequireAuth>
        <Lazy>
          <Outlet />
        </Lazy>
      </RequireAuth>
    ),
    children: [
      { path: ":id", element: <PayTransferPage /> },
      { path: ":id/proof", element: <UploadProofPage /> },
      { path: ":id/status", element: <PaymentStatusPage /> },
    ],
  },
  {
    path: "/admin",
    element: (
      <RequireAuth>
        <Lazy>
          <AdminLayout>
            <Lazy>
              <Outlet />
            </Lazy>
          </AdminLayout>
        </Lazy>
      </RequireAuth>
    ),
    children: [
      { index: true, element: admin("dashboard", <AdminDashboardPage />) },
      { path: "users", element: admin("users", <AdminUsersPage />) },
      { path: "vendors", element: admin("vendors", <AdminVendorsPage />) },
      { path: "kyc", element: admin("kyc", <AdminKycPage />) },
      { path: "listings", element: admin("listings", <AdminListingsPage />) },
      { path: "catalogue", element: admin("catalogue", <AdminCataloguePage />) },
      { path: "orders", element: admin("orders", <AdminOrdersPage />) },
      { path: "disputes", element: admin("disputes", <AdminDisputesPage />) },
      { path: "disputes/:id", element: admin("disputes", <AdminDisputeDetailPage />) },
      { path: "payments", element: admin("payment_reviews", <AdminPaymentReviewPage />) },
      { path: "payments/settings", element: admin("payment_reviews", <AdminPaymentSettingsPage />) },
      { path: "payments/flutterwave", element: admin("payments", <AdminPaymentsPage />) },
      { path: "wallets", element: admin("wallets", <AdminWalletsPage />) },
      { path: "payouts", element: admin("payouts", <AdminPayoutsPage />) },
      { path: "subscriptions", element: admin("subscriptions", <AdminSubscriptionsPage />) },
      { path: "plans", element: admin("plans", <AdminPlansPage />) },
      { path: "ads", element: admin("ads", <AdminAdsPage />) },
      { path: "promotions", element: admin("promotions", <AdminPromotionsPage />) },
      { path: "cms", element: admin("cms", <AdminCmsPage />) },
      { path: "notifications", element: admin("notifications", <AdminNotificationsPage />) },
      { path: "notifications/broadcast", element: admin("notifications", <AdminBroadcastPage />) },
      { path: "reviews", element: admin("reviews", <AdminReviewsPage />) },
      { path: "store-photos", element: admin("vendors", <AdminStorePhotosPage />) },
      { path: "chats", element: admin("chats", <AdminChatsPage />) },
      { path: "support", element: admin("support", <AdminSupportPage />) },
      { path: "reports", element: admin("reports", <AdminReportsPage />) },
      { path: "settings", element: admin("settings", <AdminSettingsPage />) },
      { path: "team", element: admin("team", <AdminTeamPage />) },
      { path: "audit-log", element: admin("audit", <AdminAuditLogPage />) },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      {
        element: <RequireGuest />,
        children: [
          { path: "/login", element: lazyEl(<LoginPage />) },
          { path: "/register", element: lazyEl(<RegisterPage />) },
          { path: "/forgot-password", element: lazyEl(<ForgotPasswordPage />) },
        ],
      },
      { path: "/reset-password", element: lazyEl(<ResetPasswordPage />) },
      { path: "/verify", element: lazyEl(<VerifyPage />) },
    ],
  },
  ...devRoutes,
  {
    element: <StoreLayout />,
    children: [{ path: "*", element: <NotFoundPage /> }],
  },
];

/** Every top-level branch shows the branded fallback (not the router's default) if a page throws. */
export const routes: RouteObject[] = appRoutes.map((r) => ({ ...r, errorElement: <RouteError /> }));

export const router = createBrowserRouter(routes, {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});
